@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.support

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlin.coroutines.resume
import org.example.project.engineletter.RenderSupportLetterSys
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.service.CustomerApi
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

internal const val SYS_SUPPORT_PDF_PREVIEW_ROOT_ID = "sys-support-pdf-preview-root"
private const val SYS_SUPPORT_PDF_RENDER_ATTEMPTS = 12

@Composable
fun SysSupportScreen() {
    val scope = rememberCoroutineScope()
    val supportLiveVersion = GlobalManager.jsonDomainVersion("support")
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var tickets by remember { mutableStateOf<List<All>>(emptyList()) }
    var ticketFilter by remember { mutableStateOf("ALLE") }
    var selectedTicketId by remember { mutableStateOf<String?>(null) }
    var manuelleLoesung by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var defaultLogoSrc by remember { mutableStateOf("") }
    var unternehmen by remember { mutableStateOf(All()) }
    var currentSysUser by remember { mutableStateOf(All()) }
    var selectedTicketPdfUrl by remember { mutableStateOf("") }
    var selectedKunde by remember { mutableStateOf<All?>(null) }
    var pdfPreviewTicket by remember { mutableStateOf<All?>(null) }

    suspend fun reload() {
        tickets = ServiceApi.loadSupport("all")
            .sortedByDescending { it.id.toLongOrNull() ?: 0L }
    }

    LaunchedEffect(supportLiveVersion) {
        reload()
        defaultLogoSrc = LogoSettings.supportLetterSysLogo
        currentSysUser = LoginService.getUser() ?: All()
        unternehmen = runCatching { UnternehmenstammApi.loadUnternehmen() ?: All() }.getOrDefault(All())
    }

    val selectedTicket = tickets.firstOrNull { it.id == selectedTicketId }
    val offeneTickets = tickets.count { it.zustand != ZustandManager.Support.ABGESCHLOSSEN }
    val abgeschlosseneTickets = tickets.count { it.zustand == ZustandManager.Support.ABGESCHLOSSEN }
    val filteredTickets = when (ticketFilter) {
        "OFFEN" -> tickets.filter { it.zustand != ZustandManager.Support.ABGESCHLOSSEN }
        "CLOSE" -> tickets.filter { it.zustand == ZustandManager.Support.ABGESCHLOSSEN }
        else -> tickets
    }

    fun openTicket(ticket: All) {
        scope.launch {
            if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
                selectedTicketId = null
                ServiceApi.openSupportDocument(ticket.id, ServiceApi.supportPdfFileName(ticket))
                return@launch
            }
            selectedTicketId = ticket.id
            manuelleLoesung = ticket.manuelleLoesung
            selectedKunde = ticket.firmenId.takeIf { it.isNotBlank() }?.let { CustomerApi.loadDetails(it) }
        }
    }

    suspend fun exportSupportPdf(ticket: All): Boolean {
        val ticketId = ticket.id
        if (ticketId.isBlank()) return false
        selectedKunde = ticket.firmenId.takeIf { it.isNotBlank() }?.let { CustomerApi.loadDetails(it) } ?: selectedKunde
        pdfPreviewTicket = ticket
        awaitSysSupportAnimationFrame()
        try {
            val root = waitForSysSupportPdfRoot(SYS_SUPPORT_PDF_PREVIEW_ROOT_ID) ?: return false
            return runCatching {
                val fileName = ServiceApi.supportPdfFileName(ticket)
                val pdfFile = renderLetterDocumentPdfFile(root, fileName)
                val pdfFileSize = (pdfFile.asDynamic().size as? Number)?.toDouble() ?: 0.0
                if (pdfFileSize <= 0.0) return false
                selectedTicketPdfUrl = ServiceApi.uploadServiceDocument("support", ticketId, pdfFile, fileName)
                selectedTicketPdfUrl.isNotBlank()
            }.onFailure { error ->
                console.error("Support PDF export failed", error)
            }.getOrDefault(false)
        } finally {
            pdfPreviewTicket = null
        }
    }

    suspend fun closeSelectedTicket(): Boolean {
        val ticket = selectedTicket ?: return false
        val manualTicket = ticket.copy(
            loesungsweg = manuelleLoesung,
            ticketgrund = ticket.loesungsweg,
            manuelleFehlerursache = ticket.loesungsweg,
            manuelleLoesung = manuelleLoesung
        )
        val pdfSaved = exportSupportPdf(manualTicket)
        if (!pdfSaved) {
            AppDialogs.alert("Support-PDF konnte nicht gespeichert werden. Ticket wurde nicht abgeschlossen.")
            return false
        }
        val updated = ServiceApi.closeSupportManual(
            ticketId = ticket.id,
            fehlerursache = ticket.loesungsweg,
            loesung = manuelleLoesung,
            kostenpflichtig = ticket.kostenpflichtig
        ) ?: return false
        selectedTicketPdfUrl = ""
        selectedTicketId = null
        reload()
        return true
    }

    SysSupportLayout(
        internalTitleVisible = internalTitleVisible,
        tickets = tickets,
        filteredTickets = filteredTickets,
        ticketFilter = ticketFilter,
        offeneTickets = offeneTickets,
        abgeschlosseneTickets = abgeschlosseneTickets,
        selectedTicket = selectedTicket,
        manuelleLoesung = manuelleLoesung,
        saving = saving,
        onFilterChange = { ticketFilter = it },
        onBack = {
            selectedTicketId = null
        },
        onOpenTicket = ::openTicket,
        onManualSolutionChange = { manuelleLoesung = it },
        onCloseTicket = {
            if (manuelleLoesung.isBlank()) return@SysSupportLayout
            saving = true
            scope.launch {
                try {
                    closeSelectedTicket()
                } finally {
                    saving = false
                }
            }
        }
    )

    val renderedTicket = pdfPreviewTicket ?: selectedTicket
    if (renderedTicket != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                opacity(0)
                property("z-index", "-1")
            }
        }) {
            RenderSupportLetterSys(
                rootElementId = SYS_SUPPORT_PDF_PREVIEW_ROOT_ID,
                ticket = renderedTicket,
                kunde = selectedKunde,
                unternehmen = unternehmen,
                logoSrc = defaultLogoSrc,
                manuelleLoesung = manuelleLoesung,
                signaturBildSrc = currentSysUser.signatur,
                signaturNameText = listOf(currentSysUser.vorname, currentSysUser.nachname)
                    .filter { it.isNotBlank() }
                    .joinToString(" ")
                    .ifBlank { currentSysUser.personalnummer }
                    .ifBlank { currentSysUser.id }
            )
        }
    }
}

private suspend fun waitForSysSupportPdfRoot(rootId: String): HTMLElement? {
    repeat(SYS_SUPPORT_PDF_RENDER_ATTEMPTS) {
        awaitSysSupportAnimationFrame()
        val root = document.getElementById(rootId) as? HTMLElement
        if (root?.querySelector("[data-letter-pdf-page='true']") != null) return root
    }
    return document.getElementById(rootId) as? HTMLElement
}

private suspend fun awaitSysSupportAnimationFrame() {
    kotlinx.coroutines.suspendCancellableCoroutine<Unit> { continuation ->
        window.requestAnimationFrame {
            continuation.resume(Unit)
        }
    }
}
